import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { Modal, ModalBody } from 'reactstrap';
import Select from 'react-select/async';
import debounce from 'debounce-promise';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

import { PROVISION_MODEL_ID_SET } from '../../Constants/StaticUUIDs';
import AssetService from '../../Services/AssetService';
import TemplateService from '../../Services/TemplateService';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';

export default memo(ValidateAssetModal);

const initState = { error: '', requestPending: false, searchPending: true };

function ValidateAssetModal(props) {
  const [state, updateState] = useState(initState);

  const assetService = useMemo(() => new AssetService(), []);
  const templateService = useMemo(() => new TemplateService(), []);
  let source = CancelToken.source();

  const fetchModels = async (search = '') => {
    try {
      updateState((prevState) => ({
        ...prevState,
        error: '',
        searchPending: true,
      }));

      source.cancel();
      source = CancelToken.source();

      const { data } = await templateService.fetchPaginatedTemplates(
        1,
        props.asset?.orgID,
        50,
        'name',
        'asc',
        search,
        props.asset?.vendor,
        source.token
      );

      updateState((prevState) => ({
        ...prevState,
        error: '',
        searchPending: false,
      }));
      return data
        .filter(({ _id }) => !PROVISION_MODEL_ID_SET.has(_id))
        .map(({ _id, name }) => ({ label: name, value: _id }));
    } catch (err) {
      if (isCancel) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        searchPending: false,
      }));
      return [];
    }
  };

  const handleModelSelected = (selectedModel) => {
    updateState((prevState) => ({ ...prevState, error: '', selectedModel }));
  };

  const handleStartClick = async () => {
    try {
      updateState((prevState) => ({
        ...prevState,
        error: '',
        requestPending: true,
      }));

      await assetService.validateAsset(
        props.asset?._id,
        state.selectedModel?.value,
        source.token
      );
      props.handleValidateAssetSuccess();
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  };

  useEffect(() => source.cancel, []);

  return (
    <Modal
      centered
      isOpen={props.isOpen}
      scrollable
      size="lg"
      toggle={props.toggleModal}
    >
      <ModalBody className="container-fluid py-4">
        <div className="row align-items-center justify-content-center">
          <h5 className="col-12 col-md-10 modal-title-text">Validate Asset</h5>
        </div>
        <div className="row align-items-center justify-content-center">
          <p className="col-12 col-md-10 modal-supplemental-text text-center text-md-left">
            Validate the Asset against a User created Asset Model without
            activating a subscription. This validation process is temporary and
            will automatically set it back to the Provisioning Model after 4
            hours.
          </p>
        </div>
        <div className="row align-items-center justify-content-center mt-3">
          <div className="col-12 col-md-10 form-group">
            <label className="modal-inputlabel-text" htmlFor="assetModel">
              Asset Model
            </label>
            <div className="d-flex">
              <Select
                cacheOptions
                captureMenuScroll={false}
                defaultOptions
                className="flex-grow-1"
                isClearable
                isDisabled={state.requestPending}
                isLoading={state.searchPending}
                isSearchable
                loadingMessage={() => 'Searching Asset Models...'}
                loadOptions={debounce(fetchModels, 1000)}
                maxMenuHeight={250}
                menuShouldScrollIntoView
                name="assetModel"
                noOptionsMessage={({ inputValue }) =>
                  `No models match: ${inputValue}`
                }
                onChange={handleModelSelected}
                placeholder="Select an Asset Model"
                styles={BASIC_SELECT_STYLES}
                value={state.selectedModel}
              />
              <button
                className="btn btn-sm modal-button-submit ml-3 px-md-4 nine-tenth-text"
                disabled={state.requestPending || !state.selectedModel?.value}
                onClick={handleStartClick}
              >
                {state.requestPending ? (
                  <FontAwesomeIcon icon={faCircleNotch} spin />
                ) : (
                  'Start'
                )}
              </button>
            </div>
          </div>
        </div>
        <div
          className="row align-items-end justify-content-end"
          style={{ height: '250px' }}
        >
          <p className="col-12 col-md-10 text-right three-quarter-text text-danger">
            {state.error}
          </p>
        </div>
      </ModalBody>
    </Modal>
  );
}

ValidateAssetModal.propTypes = {
  asset: PropTypes.shape({
    _id: PropTypes.string,
    orgID: PropTypes.string,
    vendor: PropTypes.string,
  }),
  handleValidateAssetSuccess: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};
